import React, { FunctionComponent, useEffect, useState } from "react";
import styled from "styled-components";
import Column from "../../../components/Column";
import Input from "../../../components/Input";
import Button from "../../../components/Button";
import { useProspectsHandlers } from "../../../services/actions/prospects/useProspectsHandlers";
import { styles } from "../../../styles/themes/style";
import ProspectsTable from "../components/prospects/ProspectsTable";
import { useConnectionsHandlers } from "../../../services/actions/connections/useConnectionsHandlers";
import InputAutocomplete from "../../../components/InputAutocomplete";
import AdvancedSearchDropdown from "../../../fragments/AdvancedSearchDropdown";
import { prospectIndustryOptions } from "../../../assets/utils/prospectIndustryOptions";
import { countryOptions } from "../../../assets/utils/countryOptions";
import SvgFragment from "../../../fragments/SvgFragment";
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../interfaces/store/appStore';
import Text from '../../../components/Text';
import {
  getProspectsSearchCache,
  resetProspectsSearchCache,
  setProspectsSearchCache,
} from './prospectsSearchCache';

interface IProspectsSectionProps {}

/**
 * Groups consecutive AND-connected titles into comma-separated strings.
 * Each resulting string becomes a separate repeated param (OR between groups).
 * e.g. [A] AND [B] OR [C] → ["A,B", "C"] → job_title=A,B&job_title=C
 */
const buildJobTitlePayload = (titles: string[], operators: ('and' | 'or')[]): string[] => {
  if (titles.length === 0) return [];
  if (titles.length === 1) return [titles[0]];
  const groups: string[][] = [[titles[0]]];
  for (let i = 1; i < titles.length; i++) {
    const op = operators[i - 1] ?? 'or';
    if (op === 'and') {
      groups[groups.length - 1].push(titles[i]);
    } else {
      groups.push([titles[i]]);
    }
  }
  return groups.map((g) => g.join(','));
};

const initialFields = {
  state: "",
  city: "",
  countryCode: "",
  jobTitle: [] as string[],
  school: "",
  companyName: "",
  companyDomain: "",
  companyLinkedinUrl: "",
  companyLinkedinId: "",
  companyState: "",
  companyCity: "",
  companyCountryCode: "",
  companyIndustry: "",
  companySize: "",
  companyFounded: "",
  companyMinFounded: "",
  companyMaxFounded: "",
  nextPageToken: "",
};

const ProspectsSection: FunctionComponent<IProspectsSectionProps> = () => {
  // Rehydrate from the in-memory cache so the user's last LeadSearch session
  // (filters, results, pagination, selection) is restored when they return
  // to this tab from Contacts or any other page within the app.
  const cachedSearch = getProspectsSearchCache();

  const [selectedProfiles, setSelectedProfiles] = useState<any[]>(
    cachedSearch?.selectedProfiles ?? []
  );
  const [isAllSelected, setIsAllSelected] = useState<boolean>(
    cachedSearch?.isAllSelected ?? false
  );
  const {
    getProspectsHandler,
    getProspectsTrialLimitHandler,
    autocompleteCompaniesHandler,
    autocompleteJobTitlesHandler,
    autocompleteLocationsHandler,
  } = useProspectsHandlers();
  const { addToLeadDeltaHandler, addToLeadDeltaBulkHandler } =
    useConnectionsHandlers();

  const [fields, setFields] = useState(cachedSearch?.fields ?? initialFields);
  const [profiles, setProfiles] = useState<any[]>(cachedSearch?.profiles ?? []);
  const [profilesCount, setProfilesCount] = useState<number>(
    cachedSearch?.profilesCount ?? 0
  );
  const [nextPageToken, setNextPageToken] = useState<string>(
    cachedSearch?.nextPageToken ?? ""
  );
  const [currentPageToken, setCurrentPageToken] = useState<string>(
    cachedSearch?.currentPageToken ?? ""
  );
  const [visibleCount, setVisibleCount] = useState(
    cachedSearch?.visibleCount ?? 20
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isBulkAdding, setIsBulkAdding] = useState(false);

  const [companiesSuggestions, setCompaniesSuggestions] = useState<string[]>(
    []
  );
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState<string[]>([]);
  const [jobTitleInputValue, setJobTitleInputValue] = useState(
    cachedSearch?.jobTitleInputValue ?? ""
  );
  const [jobTitleOperators, setJobTitleOperators] = useState<('and' | 'or')[]>(
    cachedSearch?.jobTitleOperators ?? []
  );
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [countrySuggestions, setCountrySuggestions] = useState<string[]>([]);

  const handleDataChange = (selectedProfiles: any[]) => {
    setSelectedProfiles(selectedProfiles);
  };

  const updateField = (field: string, value: string) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const addJobTitle = (title: string) => {
    const trimmed = title.trim();
    if (!trimmed || fields.jobTitle.includes(trimmed)) return;
    setFields((prev) => ({ ...prev, jobTitle: [...prev.jobTitle, trimmed] }));
    if (fields.jobTitle.length > 0) {
      setJobTitleOperators((prev) => [...prev, 'or']);
    }
    setJobTitleInputValue("");
    setJobTitleSuggestions([]);
  };

  const removeJobTitle = (index: number) => {
    setFields((prev) => ({
      ...prev,
      jobTitle: prev.jobTitle.filter((_, i) => i !== index),
    }));
    setJobTitleOperators((prev) => {
      if (prev.length === 0) return prev;
      const opIdx = index < prev.length ? index : index - 1;
      return [...prev.slice(0, opIdx), ...prev.slice(opIdx + 1)];
    });
  };

  const toggleJobTitleOperator = (index: number) => {
    setJobTitleOperators((prev) => {
      const next = [...prev];
      next[index] = next[index] === 'and' ? 'or' : 'and';
      return next;
    });
  };

  const [lastPayload, setLastPayload] = useState<any>(cachedSearch?.lastPayload ?? {});

  // Keep the module-level cache in sync so a later remount of this component
  // (e.g. after navigating to another tab and back) can fully restore the view.
  useEffect(() => {
    setProspectsSearchCache({
      fields,
      jobTitleInputValue,
      jobTitleOperators,
      profiles,
      profilesCount,
      nextPageToken,
      currentPageToken,
      visibleCount,
      lastPayload,
      selectedProfiles,
      isAllSelected,
    });
  }, [
    fields,
    jobTitleInputValue,
    jobTitleOperators,
    profiles,
    profilesCount,
    nextPageToken,
    currentPageToken,
    visibleCount,
    lastPayload,
    selectedProfiles,
    isAllSelected,
  ]);

  const buildPayload = (extra?: Record<string, any>) => {
    const base: Record<string, any> = Object.fromEntries(
      Object.entries(fields).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== "" && v !== undefined
      )
    );
    if (Array.isArray(base.jobTitle) && base.jobTitle.length > 0) {
      base.jobTitle = buildJobTitlePayload(base.jobTitle, jobTitleOperators);
    }
    return { ...base, ...extra };
  };

  const companySizeOptions = [
    { label: "1", value: "1" },
    { label: "2-10", value: "2-10" },
    { label: "11-50", value: "11-50" },
    { label: "51-200", value: "51-200" },
    { label: "201-500", value: "201-500" },
    { label: "501-1000", value: "501-1000" },
    { label: "1001-5000", value: "1001-5000" },
    { label: "5001-10000", value: "5001-10000" },
    { label: "10001+", value: "10001+" },
  ];

  const { store: { trial, workspace, workspaceOwner } } = useAuthStore();
  const isStarter = workspace?.isStarter;
  // Trial users (non-starter) are allowed to use LeadSearch but without pagination:
  // the backend caps the total results and rejects any nextPageToken for them.
  const isTrialLimited = !workspace?.prospectsEnabled && !!trial && !isStarter;

  // Limit surfaced by the backend (DataPartnershipControler) so the banner and
  // error text always use the same configurable number as the server.
  const [trialSearchLimit, setTrialSearchLimit] = useState<number | null>(null);
  const [trialLimitExpiredMessage, setTrialLimitExpiredMessage] = useState<
    string | null
  >(null);
  const trialLimitExpired = !!trialLimitExpiredMessage;

  // Fetch the configurable trial limit on mount so the banner can display the
  // exact number even before the user triggers a search.
  useEffect(() => {
    if (!isTrialLimited) return;
    let cancelled = false;
    (async () => {
      const res = await getProspectsTrialLimitHandler();
      if (cancelled) return;
      if (typeof res?.leadSearchTrialSearchLimit === "number") {
        setTrialSearchLimit(res.leadSearchTrialSearchLimit);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTrialLimited]);

  const handleGetProspects = async () => {
    setIsLoading(true);

    const payload = buildPayload();
    const fieldsChanged = JSON.stringify(payload) !== JSON.stringify(lastPayload);

    let useNextPageToken = false;

    if (fieldsChanged) {
      setProfiles([]);
      setVisibleCount(0);
      setCurrentPageToken("");
      setNextPageToken("");
      setLastPayload(payload);
    } else if (nextPageToken && !isTrialLimited) {
      payload.nextPageToken = nextPageToken;
      useNextPageToken = true;
    }

    const res = await getProspectsHandler(payload);
    const data = res?.data;

    // Detect the trial-expired error surfaced by the backend so we can show a
    // persistent banner (in addition to the error toast raised by useAxios).
    if (res && res.success === false) {
      if (
        isTrialLimited &&
        typeof res.message === "string" &&
        /trial LeadSearch .* search limit has been reached/i.test(res.message)
      ) {
        setTrialLimitExpiredMessage(res.message);
      }
      setIsLoading(false);
      return;
    }
    setTrialLimitExpiredMessage(null);
    if (isTrialLimited && typeof data?.leadSearchTrialSearchLimit === "number") {
      setTrialSearchLimit(data.leadSearchTrialSearchLimit);
    }

    if (useNextPageToken && nextPageToken && nextPageToken !== "") {
      setProfiles([...profiles, ...(data?.profiles || [])]);
      setVisibleCount((profiles?.length || 0) + (data?.profiles?.length || 0));
    } else {
      setProfiles(data?.profiles || []);
      setVisibleCount(data?.profiles?.length || 0);
    }

    setProfilesCount(data?.count || 0);
    setCurrentPageToken(nextPageToken);
    setNextPageToken(isTrialLimited ? "" : data?.nextPageToken || "");
    setIsLoading(false);
  };

  const clearFilters = () => {
    setFields(initialFields);
    setJobTitleInputValue("");
    setJobTitleOperators([]);
    setJobTitleSuggestions([]);
    setProfiles([]);
    setProfilesCount(0);
    setNextPageToken("");
    setCurrentPageToken("");
    setVisibleCount(20);
    setLastPayload({});
    setSelectedProfiles([]);
    setIsAllSelected(false);
    setTrialLimitExpiredMessage(null);
    resetProspectsSearchCache();
  };

  const handleAddToLeadDelta = async (row: any) => {
    const result = await addToLeadDeltaHandler(row);
    if (result && result.publicIdentifier) {
      const payload = buildPayload(currentPageToken ? { nextPageToken: currentPageToken } : undefined);
      const res = await getProspectsHandler(payload);
      const data = res?.data;

      if (data && Array.isArray(data.profiles)) {
        setProfiles((prevProfiles) => {
          const updatedProfile = data.profiles.find(
            (p: any) => p.linkedin_id === result.publicIdentifier
          );
          if (!updatedProfile) return prevProfiles;
          return prevProfiles.map((profile) =>
            profile.linkedin_id === result.publicIdentifier
              ? updatedProfile
              : profile
          );
        });
      }
    }
  };

  const handleBulkAddToLeadDelta = async () => {
    setIsBulkAdding(true);
    const result = await addToLeadDeltaBulkHandler({
      profiles: selectedProfiles.map((row) => ({
        followerCount: row.followerCount ?? undefined,
        profileId: row.profileId ?? row.linkedin_id ?? undefined,
        entityUrn: row.entityUrn ?? undefined,
        firstName:
          row.firstName ?? (row.name ? row.name.split(" ")[0] : undefined),
        lastName:
          row.lastName ??
          (row.name ? row.name.split(" ").slice(1).join(" ") : undefined),
        headline: row.headline ?? row.title ?? undefined,
        profilePicture: row.image_url ?? row.profilePicture ?? undefined,
        location: row.location
          ? row.location.address
            ? `${row.location.address}`
            : row.location.city || undefined
          : undefined,
        country: row.location?.state ?? undefined,
        industry: row.industry ?? undefined,
        company: row.company?.name ?? row.company ?? undefined,
        jobTitle: row.jobTitle ?? row.position ?? undefined,
        languages: Array.isArray(row?.languages)
          ? row.languages?.flat()
          : typeof row?.languages === "object" && row?.languages !== null
          ? Object.values(row.languages).flat()
          : [],
        publicIdentifier: row.publicIdentifier ?? row.linkedin_id ?? undefined,
      })),
      isProspect: true,
    });
    if (
      result &&
      result.results &&
      Array.isArray(result.results) &&
      result.results.length > 0
    ) {
      const payload = buildPayload(currentPageToken ? { nextPageToken: currentPageToken } : undefined);
      const res = await getProspectsHandler(payload);
      const data = res?.data;

      if (data && Array.isArray(data.profiles)) {
        setProfiles((prevProfiles) => {
          return prevProfiles.map((profile) => {
            const updatedProfile = data.profiles.find(
              (p: any) =>
                p.linkedin_id === profile.linkedin_id &&
                result.results.some(
                  (r: any) => r.publicIdentifier === profile.linkedin_id
                )
            );
            return updatedProfile ? updatedProfile : profile;
          });
        });
      }
    }
    setIsBulkAdding(false);
  };

  const handleAutocompleteCompany = async (value: string) => {
    if (!value.trim()) {
      setCompaniesSuggestions([]);
      return;
    }
    const suggestions = await autocompleteCompaniesHandler({ name: value });
    setCompaniesSuggestions(suggestions?.companies || []);
  };

  const handleAutocompleteJobTitle = async (value: string) => {
    if (!value.trim()) {
      setJobTitleSuggestions([]);
      return;
    }
    const suggestions = await autocompleteJobTitlesHandler({ name: value });
    setJobTitleSuggestions(suggestions?.job_titles || []);
  };

  const handleAutocompleteCities = async (value: string) => {
    if (!value.trim()) {
      setCitySuggestions([]);
      return;
    }
    const suggestions = await autocompleteLocationsHandler({ name: value });
    const uniqueCities: any[] = [];
    const seen = new Set();
    (suggestions?.locations || []).forEach((loc: any) => {
      if (loc.type === "city" && !seen.has(loc.name)) {
        seen.add(loc.name);
        uniqueCities.push(loc);
      }
    });
    setCitySuggestions(uniqueCities);
  };

  const handleAutocompleteStates = async (value: string) => {
    if (!value.trim()) {
      setCountrySuggestions([]);
      return;
    }
    const suggestions = await autocompleteLocationsHandler({ name: value });
    setCountrySuggestions(
      (suggestions?.locations || []).filter((loc: any) => loc.type === "state")
    );
  };

  const Info = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;

    &:hover .info-description-enrichment-modal {
      display: block;
    }
  `;

  const InfoDescription = styled.div<{ width: string }>`
    display: none;
    width: ${({ width }) => width};
    position: absolute;
    background-color: ${styles.colors.white};
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #dae0e8;
    left: 28px;
    top: 0;
    z-index: 10;
    font-size: 13px;
    color: #222;
    min-width: 220px;
  `;

  const InfoComponent: React.FC<{
    width?: string;
    text: string;
    correct: string;
    avoid: string;
  }> = ({ width = "260px", text, correct, avoid }) => {
    return (
      <Info>
        <StyledSvg>
          <SvgFragment type="infoCircle" />
        </StyledSvg>
        <InfoDescription
          width={width}
          className="info-description-enrichment-modal"
        >
          <div style={{ marginBottom: 8, fontWeight: 500 }}>{text}</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
            }}
          >
            <span style={{ color: "#2ecc40", fontWeight: 700, fontSize: 16 }}>
              ✔️
            </span>
            <span>
              <span style={{ fontWeight: 600 }}>Correct:</span> {correct}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#ff4136", fontWeight: 700, fontSize: 16 }}>
              ✖️
            </span>
            <span>
              <span style={{ fontWeight: 600 }}>Avoid:</span> {avoid}
            </span>
          </div>
        </InfoDescription>
      </Info>
    );
  };

  const navigate = useNavigate();

  if (!workspace?.prospectsEnabled && isStarter) {
    return (
      <CenteredContainer>
        <Text $heading3>Unlock this feature by subscribing to a Business plan.</Text>
        <div style={{width: '450px', textAlign: 'center'}}>
          <Text $paragraph1>
            You can also{' '}
            <a href="https://www.loom.com/share/28e7d345ea2a49609d857c6cae0d1e6a?sid=e748dfd7-71fe-4d7d-8906-fd6391fa65ee" target="_blank" rel="noopener noreferrer" style={{ color: '#5964FF', textDecoration: 'underline' }}>
              watch a quick demo video
            </a>{' '}or{' '}
            <a href="https://meetings.hubspot.com/ved/marketing" target="_blank" rel="noopener noreferrer" style={{ color: '#5964FF', textDecoration: 'underline' }}>
              book a demo call
            </a>.
          </Text>
        </div>
        <Column gap='16px'>
          {workspaceOwner && (
            <Button $type="blue" $bigButton onClickHandler={() => navigate(AppRoutes.BILLING)}>
              Upgrade your plan
            </Button>
          )}
        </Column>
      </CenteredContainer>
    );
  }

  return (
    <Container>
      <StyledColumn>
        {isTrialLimited && (() => {
          const bannerText = trialLimitExpired
            ? trialLimitExpiredMessage || ""
            : trialSearchLimit != null
            ? `LeadSearch is limited to a single-page result and ${trialSearchLimit} searches during a trial. Upgrade to a Business plan to unlock unlimited searches and results.`
            : "LeadSearch is limited to a single-page result during a trial. Upgrade to a Business plan to unlock unlimited searches and results.";
          // Prefer the exact "Upgrade to Business" phrase (expired copy); fall
          // back to "Upgrade to a Business plan" (info copy) so we always have
          // an inline, underlined CTA instead of a trailing duplicate link.
          const linkPhrase =
            bannerText.indexOf("Upgrade to Business") !== -1
              ? "Upgrade to Business"
              : "Upgrade to a Business plan";
          const linkIdx = bannerText.indexOf(linkPhrase);
          const before = linkIdx !== -1 ? bannerText.slice(0, linkIdx) : bannerText;
          const after =
            linkIdx !== -1 ? bannerText.slice(linkIdx + linkPhrase.length) : "";
          return (
            <TrialBanner $expired={trialLimitExpired}>
              {before}
              {linkIdx !== -1 &&
                (workspaceOwner ? (
                  <TrialBannerLink onClick={() => navigate(AppRoutes.BILLING)}>
                    {linkPhrase}
                  </TrialBannerLink>
                ) : (
                  linkPhrase
                ))}
              {after}
            </TrialBanner>
          );
        })()}
        <div
          style={{
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 700,
            marginBottom: "4px",
            color: styles.colors.black600,
          }}
        >
          Prospect filters
        </div>
        <InputsWrapper>
          <AdvancedSearchDropdown
            placeholder="Country"
            options={countryOptions}
            border="2px solid #DAE0E8"
            panelWidth="300px"
            value={
              fields.countryCode && fields.countryCode !== ""
                ? countryOptions.find(
                    (c: any) => c.value === fields.countryCode
                  )
                : { value: null, label: "All Countries" }
            }
            onClick={(e: any) => updateField("countryCode", e.value)}
          />
          <InputAutocomplete
            value={fields.state}
            onChange={(v) => {
              updateField("state", v);
              handleAutocompleteStates(v);
            }}
            suggestions={countrySuggestions}
            onSuggestionSelect={(s) => {
              updateField("state", s.name);
            }}
            placeholder="State"
            onEnter={handleGetProspects}
          />
          <InputAutocomplete
            value={fields.city}
            onChange={(v) => {
              updateField("city", v);
              handleAutocompleteCities(v);
            }}
            suggestions={citySuggestions}
            onSuggestionSelect={(s) => {
              updateField("city", s.name);
            }}
            placeholder="City"
            onEnter={handleGetProspects}
          />
          <InputAutocomplete
            value={jobTitleInputValue}
            onChange={(v) => {
              setJobTitleInputValue(v);
              handleAutocompleteJobTitle(v);
            }}
            suggestions={jobTitleSuggestions}
            onSuggestionSelect={(s) => {
              addJobTitle(s.name);
            }}
            placeholder="Job Title"
            onEnter={() => {
              if (jobTitleInputValue.trim()) {
                addJobTitle(jobTitleInputValue);
              } else {
                handleGetProspects();
              }
            }}
          />
          <div style={{ position: "relative", display: "inline-block" }}>
            <StyledInput
              placeholder="School"
              value={fields.school}
              onChangeHandler={(v: string) => updateField("school", v)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  handleGetProspects();
                }
              }}
              style={{ paddingRight: fields.school ? 28 : undefined }}
            />
            {fields.school && (
              <span
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: 16,
                  zIndex: 2,
                  userSelect: "none",
                }}
                onClick={() => updateField("school", "")}
                title="Clear"
              >
                &#10005;
              </span>
            )}
          </div>
        </InputsWrapper>
        <div
          style={{
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 700,
            marginBottom: "4px",
            color: styles.colors.black600,
          }}
        >
          Company filters
        </div>
        <InputsWrapper>
          <InputAutocomplete
            value={fields.companyName}
            onChange={(v) => {
              updateField("companyName", v);
              handleAutocompleteCompany(v);
            }}
            suggestions={companiesSuggestions}
            onSuggestionSelect={(s) => {
              updateField("companyName", s.name);
            }}
            placeholder="Company Name"
            onEnter={handleGetProspects}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "4px",
              position: "relative",
              width: "200px",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Input
                placeholder="Company Domain"
                value={fields.companyDomain}
                onChangeHandler={(v: string) => updateField("companyDomain", v)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    // Prevent Get Prospects if $error is true
                    const isError =
                      !!fields.companyDomain &&
                      !/^(?!https?:\/\/|www\.)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/.test(
                        fields.companyDomain
                      );
                    if (!isError) {
                      handleGetProspects();
                    }
                  }
                }}
                $error={
                  !!fields.companyDomain &&
                  !/^(?!https?:\/\/|www\.)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/.test(
                    fields.companyDomain
                  )
                }
              />
              {fields.companyDomain && (
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#aaa",
                    fontSize: 16,
                    zIndex: 2,
                    userSelect: "none",
                  }}
                  onClick={() => updateField("companyDomain", "")}
                  title="Clear"
                >
                  &#10005;
                </span>
              )}
            </div>
            <InfoComponent
              width="400px"
              text="Enter the domain in this format: google.com"
              correct="google.com"
              avoid="https://google.com, www.google.com, google.com/"
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "4px",
              position: "relative",
              width: "200px",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Input
                placeholder="Company LinkedIn URL"
                value={fields.companyLinkedinUrl}
                onChangeHandler={(v: string) =>
                  updateField("companyLinkedinUrl", v)
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    // Prevent Get Prospects if $error is true
                    const isError =
                      !!fields.companyLinkedinUrl &&
                      fields.companyLinkedinUrl.endsWith("/");
                    if (!isError) {
                      handleGetProspects();
                    }
                  }
                }}
                $error={
                  !!fields.companyLinkedinUrl &&
                  fields.companyLinkedinUrl.endsWith("/")
                }
              />
              {fields.companyLinkedinUrl && (
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#aaa",
                    fontSize: 16,
                    zIndex: 2,
                    userSelect: "none",
                  }}
                  onClick={() => updateField("companyLinkedinUrl", "")}
                  title="Clear"
                >
                  &#10005;
                </span>
              )}
            </div>
            <InfoComponent
              width="380px"
              text="Use the full LinkedIn company URL, without a trailing slash."
              correct="https://www.linkedin.com/company/linkedin"
              avoid="https://www.linkedin.com/company/linkedin/"
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "4px",
              position: "relative",
              width: "200px",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Input
                placeholder="Company LinkedIn ID"
                value={fields.companyLinkedinId}
                onChangeHandler={(v: string) =>
                  updateField("companyLinkedinId", v)
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    // Prevent Get Prospects if $error is true
                    const isError =
                      !!fields.companyLinkedinId &&
                      !/^[a-zA-Z0-9-]+$/.test(fields.companyLinkedinId);
                    if (!isError) {
                      handleGetProspects();
                    }
                  }
                }}
                $error={
                  !!fields.companyLinkedinId &&
                  !/^[a-zA-Z0-9-]+$/.test(fields.companyLinkedinId)
                }
              />
              {fields.companyLinkedinId && (
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#aaa",
                    fontSize: 16,
                    zIndex: 2,
                    userSelect: "none",
                  }}
                  onClick={() => updateField("companyLinkedinId", "")}
                  title="Clear"
                >
                  &#10005;
                </span>
              )}
            </div>
            <InfoComponent
              width="460px"
              text="Enter only the company ID (the part after /company/ in the LinkedIn URL)."
              correct="linkedin"
              avoid="/linkedin, linkedin/, or the full URL"
            />
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <AdvancedSearchDropdown
              placeholder="Company Country"
              options={countryOptions}
              border="2px solid #DAE0E8"
              panelWidth="300px"
              value={
                fields.companyCountryCode && fields.companyCountryCode !== ""
                  ? countryOptions.find(
                      (c: any) => c.value === fields.companyCountryCode
                    )
                  : { value: null, label: "All Company Countries" }
              }
              onClick={(e: any) => updateField("companyCountryCode", e.value)}
            />
          </div>

          <InputAutocomplete
            value={fields.companyState}
            onChange={(v) => {
              updateField("companyState", v);
              handleAutocompleteStates(v);
            }}
            suggestions={countrySuggestions}
            onSuggestionSelect={(s) => {
              updateField("companyState", s.name);
            }}
            placeholder="Company State"
            onEnter={handleGetProspects}
          />

          <InputAutocomplete
            value={fields.companyCity}
            onChange={(v) => {
              updateField("companyCity", v);
              handleAutocompleteCities(v);
            }}
            suggestions={citySuggestions}
            onSuggestionSelect={(s) => {
              updateField("companyCity", s.name);
            }}
            placeholder="Company City"
            onEnter={handleGetProspects}
          />

          <AdvancedSearchDropdown
            placeholder="Company Industry"
            options={prospectIndustryOptions}
            border="2px solid #DAE0E8"
            panelWidth="300px"
            value={
              fields.companyIndustry && fields.companyIndustry !== ""
                ? {
                    value: fields.companyIndustry,
                    label: fields.companyIndustry,
                  }
                : { value: null, label: "All Industries" }
            }
            onClick={(e: any) => updateField("companyIndustry", e.value)}
          />
          <AdvancedSearchDropdown
            placeholder="Company headcount"
            options={companySizeOptions}
            border="2px solid #DAE0E8"
            value={
              fields.companySize && fields.companySize !== ""
                ? {
                    value: fields.companySize,
                    label: fields.companySize,
                  }
                : { value: null, label: "All Sizes" }
            }
            onClick={(e: any) => updateField("companySize", e.value)}
          />
          <div style={{ position: "relative", display: "inline-block" }}>
            <StyledInput
              placeholder="Company Founded"
              value={fields.companyFounded}
              onChangeHandler={(v: string) => updateField("companyFounded", v)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  handleGetProspects();
                }
              }}
            />
            {fields.companyFounded && (
              <span
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: 16,
                  zIndex: 2,
                  userSelect: "none",
                }}
                onClick={() => updateField("companyFounded", "")}
                title="Clear"
              >
                &#10005;
              </span>
            )}
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <StyledInput
              placeholder="Company Min Founded"
              value={fields.companyMinFounded}
              onChangeHandler={(v: string) =>
                updateField("companyMinFounded", v)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  handleGetProspects();
                }
              }}
            />
            {fields.companyMinFounded && (
              <span
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: 16,
                  zIndex: 2,
                  userSelect: "none",
                }}
                onClick={() => updateField("companyMinFounded", "")}
                title="Clear"
              >
                &#10005;
              </span>
            )}
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <StyledInput
              placeholder="Company Max Founded"
              value={fields.companyMaxFounded}
              onChangeHandler={(v: string) =>
                updateField("companyMaxFounded", v)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  handleGetProspects();
                }
              }}
            />
            {fields.companyMaxFounded && (
              <span
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: 16,
                  zIndex: 2,
                  userSelect: "none",
                }}
                onClick={() => updateField("companyMaxFounded", "")}
                title="Clear"
              >
                &#10005;
              </span>
            )}
          </div>
        </InputsWrapper>
        {fields.jobTitle.length > 0 && (
          <JobTitleTagsRow>
            {fields.jobTitle.map((title, i) => (
              <React.Fragment key={title + i}>
                {i > 0 && (
                  <JobTitleLabel style={{ padding: "0px 4px" }}>
                    <JobTitleOperatorOption
                      $active={jobTitleOperators[i - 1] === 'and'}
                      onClick={() => toggleJobTitleOperator(i - 1)}
                    >
                      And
                    </JobTitleOperatorOption>
                    <JobTitleSeparator />
                    <JobTitleOperatorOption
                      $active={jobTitleOperators[i - 1] !== 'and'}
                      onClick={() => toggleJobTitleOperator(i - 1)}
                    >
                      Or
                    </JobTitleOperatorOption>
                  </JobTitleLabel>
                )}
                <JobTitleLabel style={{ padding: "2px 4px" }}>
                  <span>Job Title: {title}</span>
                  <JobTitleCloseButton
                    onClick={(e) => { e.stopPropagation(); removeJobTitle(i); }}
                    width="12"
                    height="12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.96 11.825 6 7.785l4.04 4.04a.6.6 0 0 0 .846 0l.94-.939a.6.6 0 0 0 0-.847L7.785 6l4.039-4.04a.6.6 0 0 0 0-.846l-.939-.939a.6.6 0 0 0-.847 0L6 4.215 1.96.175a.6.6 0 0 0-.846 0l-.939.939a.6.6 0 0 0 0 .847L4.215 6 .175 10.04a.6.6 0 0 0 0 .847l.94.938a.6.6 0 0 0 .846 0z"
                      fillRule="evenodd"
                    />
                  </JobTitleCloseButton>
                </JobTitleLabel>
              </React.Fragment>
            ))}
          </JobTitleTagsRow>
        )}
        <ButtonsWrapper>
          <Button
            $width="106px"
            $type="blue"
            onClickHandler={() => handleGetProspects()}
            disabled={
              trialLimitExpired ||
              Object.values(fields).every(
                (v) =>
                  v === "" ||
                  v === undefined ||
                  (Array.isArray(v) && v.length === 0)
              ) ||
              (!!fields.companyDomain &&
                !/^(?!https?:\/\/|www\.)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/.test(
                  fields.companyDomain
                )) ||
              (!!fields.companyLinkedinUrl &&
                fields.companyLinkedinUrl.endsWith("/")) ||
              (!!fields.companyLinkedinId &&
                !/^[a-zA-Z0-9-]+$/.test(fields.companyLinkedinId))
            }
          >
            Get Prospects
          </Button>
          <Button
            $width="130px"
            $type="mid-blue"
            disabled={selectedProfiles?.length === 0}
            onClickHandler={() => handleBulkAddToLeadDelta()}
            isLoading={isBulkAdding}
          >
            Add to LeadDelta
          </Button>
          <Button
            $width="101px"
            $type="red"
            disabled={Object.values(fields).every(
              (v) =>
                v === "" ||
                v === undefined ||
                (Array.isArray(v) && v.length === 0)
            )}
            onClickHandler={() => clearFilters()}
          >
            Clear Filters
          </Button>
        </ButtonsWrapper>
        <ProspectsTable
          profilesData={profiles}
          profilesCount={profilesCount}
          isAllSelectedProps={{ isAllSelected, setIsAllSelected }}
          selectedProfiles={selectedProfiles}
          selectedProfilesChange={handleDataChange}
          addToLeadDeltaHandler={handleAddToLeadDelta}
          isBulkAdding={isBulkAdding}
        />
        {!isTrialLimited && profilesCount > visibleCount && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "16px 0",
            }}
          >
            <Button
              $type="blue"
              $bigButton
              onClickHandler={() => handleGetProspects()}
              disabled={isLoading}
            >
              Load More
            </Button>
          </div>
        )}
      </StyledColumn>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100vh;
  overflow-y: auto;
`;
const StyledColumn = styled(Column)`
  height: 100%;
`;
const StyledInput = styled(Input)`
  width: 180px;
`;
const InputsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;

  width: 100%;

  & > div {
    width: 180px;
  }
`;
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;
const StyledSvg = styled.div`
  &:hover {
    cursor: pointer;
  }
  & svg {
    & path {
      fill: ${styles.colors.black300};
    }
  }
`;
const CenteredContainer = styled(Container)`
  align-items: center;
  padding-top: 40px;
  background: ${({ theme: { secondaryColor }}) => secondaryColor};
`;

const TrialBanner = styled.div<{ $expired?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  font-family: NunitoSans;
  font-size: 13px;
  line-height: 18px;
  color: ${({ $expired }) =>
    $expired ? styles.colors.red600 : styles.colors.primary600};
  background-color: ${({ $expired }) =>
    $expired ? styles.colors.red200 : styles.colors.primary100};
  border: 1px solid
    ${({ $expired }) =>
      $expired ? styles.colors.red600 : styles.colors.primary600};
  border-radius: 6px;
`;

const TrialBannerLink = styled.span`
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  color: inherit;
`;

// ── Job Title tags row ───────────────────────────────────────────────────────

const JobTitleTagsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const JobTitleLabel = styled.div`
  font-family: NunitoSans;
  font-size: 14px;
  background-color: ${styles.colors.primary100};
  border: 1px solid;
  border-color: ${styles.colors.primary600};
  color: ${styles.colors.primary600};
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 28px;
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const JobTitleCloseButton = styled.svg`
  background: none;
  border: none;
  margin-left: 8px;
  cursor: pointer;
  flex-shrink: 0;
  path {
    fill: ${styles.colors.primary600};
  }
  &:hover path {
    fill: ${styles.colors.primary500};
  }
`;

const JobTitleOperatorOption = styled.div<{ $active: boolean }>`
  padding: 2px 4px;
  font-weight: ${({ $active }) => ($active ? "bold" : "normal")};
  color: ${styles.colors.primary600};
  cursor: pointer;
`;

const JobTitleSeparator = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${styles.colors.primary600};
`;

export default ProspectsSection;
